import { Injectable } from '@angular/core'
import {
  IComplexDatepickerModel,
  IRelative,
  IShortcut,
  TRelativeMoment,
  TRelativeRef,
  TRelativeUnit,
} from './complex-datepicker.model'
import { ComplexToDatesModel } from './complex-to-dates.model'
import { ComplexShortcutsEnum } from './complex-shortcuts.enum'
import { BasicAuthService } from "../../services/basic-auth.service"
import { ComplexDatepickerTimezoneEnum } from "./complex-datepicker-timezone.enum"
import { ComplexDatepickerFilter } from "./complex-datepicker.filter"
import { HelperService } from "../../services/helper.service"

export type TimezoneSchema = {
  shortcut?: ComplexDatepickerTimezoneEnum,
  relative?: ComplexDatepickerTimezoneEnum,
  snapshot?: ComplexDatepickerTimezoneEnum
}

@Injectable({
  providedIn: 'root',
})
export class ComplexDatepickerService {

  private readonly serverTimezoneOffset: number | null = null

  public constructor(private authService: BasicAuthService) {
    this.serverTimezoneOffset = this.authService.getTimezoneOffset()
  }

  public toJsonFilterWithDates(filter: ComplexDatepickerFilter, timezoneSchema?: TimezoneSchema): any {
    const json: any = filter.toJson()

    if (filter.dtPicker) {
      const toDates: ComplexToDatesModel = this.parseDatepicker(JSON.parse(filter.dtPicker), timezoneSchema)

      if (toDates.start) {
        json[filter.getDateKeys().start] = HelperService.dateToString(toDates.start, 'yyyy-MM-dd HH:mm:ss', 'en')
      }

      if (toDates.end) {
        json[filter.getDateKeys().end] = HelperService.dateToString(toDates.end, 'yyyy-MM-dd HH:mm:ss', 'en')
      }
    }

    return json
  }

  public parseDatepicker(model: IComplexDatepickerModel, timezoneSchema?: TimezoneSchema): ComplexToDatesModel {
    switch (true) {
      case model.type === 'S':
        return this.parseRelativeObject(
          model.shortcut.relative,
          timezoneSchema?.shortcut || ComplexDatepickerTimezoneEnum.BROWSER
        )
      case model.type === 'D':
        const ds = model.dates.start ? model.dates.start : null
        const de = model.dates.end ? model.dates.end : null
        return new ComplexToDatesModel({ start: ds, end: de })
      case model.type === 'R':
        return this.parseRelativeObject(
          model.relative,
          timezoneSchema?.relative || ComplexDatepickerTimezoneEnum.BROWSER
        )
      case model.type === 'SN':
        return this.parseRelativeObject(
          model.relative,
          timezoneSchema?.snapshot || ComplexDatepickerTimezoneEnum.SERVER
        )
    }
  }

  private parseRelativeObject(model: IRelative, timezoneType: ComplexDatepickerTimezoneEnum): ComplexToDatesModel {
    const value: number = Math.abs(model.value)
    // IF moment is before THEN the end date is the reference
    if (model.moment === 'before') {
      const end_date: Date = this.getDateByRef(model.ref, timezoneType)
      const start_date: Date = this.cloneDate(end_date)
      switch (true) {
        case model.unit === 'sec':
          start_date.setSeconds(end_date.getSeconds() - value)
          break
        case model.unit === 'min':
          start_date.setMinutes(end_date.getMinutes() - value)
          break
        case model.unit === 'hour':
          start_date.setHours(end_date.getHours() - value)
          break
        case model.unit === 'day':
          start_date.setDate(end_date.getDate() - value)
          break
        case model.unit === 'week':
          start_date.setDate(end_date.getDate() - 7 * value)
          break
        case model.unit === 'month':
          start_date.setMonth(end_date.getMonth() - value)
          break
        case model.unit === 'year':
          start_date.setFullYear(end_date.getFullYear() - value)
          break
      }
      return new ComplexToDatesModel({
        start: start_date,
        end: this.getDateOrNull(end_date, timezoneType, model.ref_less),
      })
    } else {
      // IF moment is after THEN the start date is the reference
      const start_date: Date = this.getDateByRef(model.ref, timezoneType)
      const end_date: Date = this.cloneDate(start_date)
      switch (true) {
        case model.unit === 'sec':
          end_date.setSeconds(start_date.getSeconds() + value)
          break
        case model.unit === 'min':
          end_date.setMinutes(start_date.getMinutes() + value)
          break
        case model.unit === 'hour':
          end_date.setHours(start_date.getHours() + value)
          break
        case model.unit === 'day':
          end_date.setDate(start_date.getDate() + value)
          break
        case model.unit === 'week':
          end_date.setDate(start_date.getDate() + 7 * value)
          break
        case model.unit === 'month':
          end_date.setMonth(start_date.getMonth() + value)
          break
        case model.unit === 'year':
          end_date.setFullYear(start_date.getFullYear() + value)
          break
      }
      return new ComplexToDatesModel({
        start: start_date,
        end: this.getDateOrNull(end_date, timezoneType, model.ref_less),
      })
    }
  }

  private getDateByRef(ref: TRelativeRef, timezoneType: ComplexDatepickerTimezoneEnum): Date {
    const refDate: Date = this.getDateByTimezoneType(timezoneType)

    switch (ref) {
      case ComplexShortcutsEnum.NOW:
      case ComplexShortcutsEnum.CURRENT_TIME:
        return refDate
      case ComplexShortcutsEnum.CURRENT_DATE:
      case ComplexShortcutsEnum.START_OF_TODAY:
        return ComplexDatepickerService._getStartOfToday(refDate)
      case ComplexShortcutsEnum.END_OF_TODAY:
        return ComplexDatepickerService._endOfToday(refDate)
      case ComplexShortcutsEnum.LAST_MONDAY:
        return ComplexDatepickerService._getLastMonday(refDate)
      case ComplexShortcutsEnum.BEGINNING_OF_MONTH:
        return ComplexDatepickerService._getTheBeginningOfTheMonth(refDate)
      case ComplexShortcutsEnum.BEGINNING_OF_YEAR:
      case ComplexShortcutsEnum.CURRENT_YEAR:
        return ComplexDatepickerService._getTheBeginningOfTheYear(refDate)
      default:
        return new Date(ref)
    }
  }

  private cloneDate(aDate: any): Date {
    return new Date(JSON.parse(JSON.stringify(aDate)))
  }

  private getDateOrNull(
    endDate: Date,
    timezoneType: ComplexDatepickerTimezoneEnum,
    referenceLessSec?: number,
  ): Date | null {
    const currentDate: Date = this.getDateByTimezoneType(timezoneType)

    if (endDate.getTime() >= currentDate.getTime()) {
      return null
    }

    if (referenceLessSec) {
      endDate.setSeconds(endDate.getSeconds() - referenceLessSec)
    }

    return endDate
  }

  /** Shortcuts */

  public getDefaultShortcuts(): IShortcut[] {
    return [
      this.getLastNHoursShortcut(24),
      this.getPrevDayShortcut(),
      this.getLastNDaysShortcut(7),
      this.getPrevWeekShortcut(),
      this.getLastNDaysShortcut(30),
      this.getPrevMonthShortcut(),
      this.getLastNDaysShortcut(360),
      this.getPrevYearShortcut(),
      this.getCurrentYearShortcut(),
    ]
  }

  public getLastNHoursShortcut(hours: number, displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'hour',
        value: hours,
        moment: 'before',
        ref: ComplexShortcutsEnum.CURRENT_TIME,
      },
    }
    if (displayLabel) {
      shortcut.label = `Last ${hours} ${this.pluralize(hours, 'hour')}`
    }
    return shortcut
  }

  public getPrevDayShortcut(displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'day',
        value: 1,
        moment: 'before',
        ref: ComplexShortcutsEnum.START_OF_TODAY,
        ref_less: 1,
      },
    }
    if (displayLabel) {
      shortcut.label = 'Previous day (00:00:00 - 23:59:59)'
    }
    return shortcut
  }

  public getPrevWeekShortcut(displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'week',
        value: 1,
        moment: 'before',
        ref: ComplexShortcutsEnum.LAST_MONDAY,
        ref_less: 1,
      },
    }
    if (displayLabel) {
      shortcut.label = 'Previous week (calender week starting Monday at 00:00)'
    }
    return shortcut
  }

  public getLastNDaysShortcut(days: number, displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'day',
        value: days,
        moment: 'before',
        ref: ComplexShortcutsEnum.CURRENT_TIME,
      },
    }
    if (displayLabel) {
      shortcut.label = `Last ${days} ${this.pluralize(days, 'day')}`
    }
    return shortcut
  }

  public getPrevMonthShortcut(displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'month',
        value: 1,
        moment: 'before',
        ref: ComplexShortcutsEnum.BEGINNING_OF_MONTH,
        ref_less: 1,
      },
    }
    if (displayLabel) {
      shortcut.label = 'Previous month (Calender month starting 1st at 00:00)'
    }
    return shortcut
  }

  public getPrevYearShortcut(displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'year',
        value: 1,
        moment: 'before',
        ref: ComplexShortcutsEnum.BEGINNING_OF_YEAR,
        ref_less: 1,
      },
    }
    if (displayLabel) {
      shortcut.label = 'Previous Year (starting January 1st at 00:00)'
    }
    return shortcut
  }

  public getCurrentYearShortcut(displayLabel: boolean = true): IShortcut {
    const shortcut: IShortcut = {
      relative: {
        unit: 'year',
        value: 3,
        moment: 'after',
        ref: ComplexShortcutsEnum.CURRENT_YEAR,
        ref_less: 1,
      },
    };
    if (displayLabel) {
      shortcut.label = 'Current Year (starting January 1st at 00:00)'
    }
    return shortcut
  }

  /** Ref Dates */

  public static _getLastMonday(today: Date): Date {
    const monday: Date = new Date(JSON.parse(JSON.stringify(today)))
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7))
    monday.setHours(0)
    monday.setMinutes(0)
    monday.setSeconds(0)
    return monday
  }

  public static _getStartOfToday(today: Date): Date {
    return new Date(
      Date.parse(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()} 00:00:00`),
    )
  }

  public static _endOfToday(today: Date): Date {
    return new Date(
      Date.parse(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()} 23:59:59`),
    )
  }

  /* public static _startOfTomorrow(today: Date): Date {
    const endOfToday = ComplexDatepickerService._endOfToday(today)
    const startOfTomorrow = new Date(JSON.parse(JSON.stringify(endOfToday)))
    startOfTomorrow.setSeconds(startOfTomorrow.getSeconds() + 1)
    return startOfTomorrow
  } */

  public static _getTheBeginningOfTheMonth(today: Date): Date {
    return new Date(Date.parse(`${today.getFullYear()}-${today.getMonth() + 1}-01 00:00:00`))
  }

  public static _getTheBeginningOfTheYear(today: Date): Date {
    return new Date(Date.parse(`${today.getFullYear()}-01-01 00:00:00`))
  }

  public pluralize(val: number, label: string): string {
    if (val > 1) {
      return `${label}s`
    }
    return label
  }

  public getDefaultRelativeTimeUnits(): TRelativeUnit[] {
    return ['hour', 'day', 'week', 'month', 'year']
  }

  public getDefaultRelativeTimeMoments(): TRelativeMoment[] {
    return ['before']
  }

  private getDateByTimezoneType(timezoneType: ComplexDatepickerTimezoneEnum): Date {
    switch (true) {
      case timezoneType === ComplexDatepickerTimezoneEnum.SERVER && this.serverTimezoneOffset !== null:
        const offsetDifference: number = this.serverTimezoneOffset - new Date().getTimezoneOffset()
        const serverDate: Date = new Date()
        serverDate.setMinutes(serverDate.getMinutes() - offsetDifference)
        return serverDate

      case timezoneType === ComplexDatepickerTimezoneEnum.UTC:
        const offset: number = new Date().getTimezoneOffset()
        const utcDate: Date = new Date()
        utcDate.setMinutes(utcDate.getMinutes() + offset)
        return utcDate

      default:
        return new Date()
    }
  }

  /** Static */

  public static fromDatesModel(model: ComplexToDatesModel): IComplexDatepickerModel {
    return {
      type: 'D',
      dates: {
        start: model.start,
        end: model.end,
      },
    }
  }

  public static getDatepickerAfterClearChips(
    datepicker: IComplexDatepickerModel,
    isStartTimeRemoved: boolean
  ): IComplexDatepickerModel | null {
    if (datepicker.type && datepicker.type === 'D') {
      if (isStartTimeRemoved) {
        datepicker.dates.start = null
      } else {
        datepicker.dates.end = null
      }
      if (datepicker.dates?.start === null && datepicker.dates?.end === null) {
        return null
      } else {
        return datepicker
      }
    } else {
      return null
    }
  }
}
